/**
 * WEB-3399 — свой стиль организации в воронке: копия, сборка пака, реестр копий.
 *
 * Свой стиль ставится на воронку КОПИЕЙ (решение владельца 30.10 «правка
 * сохранённого воронки не меняет»): `settings.stylePackId` равен
 * `custom:<uuid v4>`, рядом — `settings.customStylePack` с тем же `id` и
 * готовым паком-основой `basePackId`. Форма копии — зеркало сервера
 * (`backend/src/ai-funnels/block-schema/custom-style-pack.ts`,
 * `customStylePackSchema`); общую таблицу случаев
 * (`custom-style-pack.cases.json`) кабинет гоняет в тесте-соседе.
 *
 * СОБРАННЫЙ ПАК — пак-основа целиком, поверх правки копии (`buildCustomStylePack`):
 * одно скругление — в четыре радиуса (у кнопки `'pill'` остаётся), рамка,
 * выбор и тень — одноимённые поля `shape`, плотность — таблица
 * `CUSTOM_STYLE_DENSITY`, липкая шапка и приглушение тарифов — поля `paywall`.
 * `id` собранного пака — `basePackId` (тип `StylePackId` не меняется: имя пака
 * в подписях берётся по нему); ключ копии живёт только в реестре и в
 * `settings.stylePackId`.
 *
 * РЕЕСТР КОПИЙ — «ключ копии → копия», только пополняется: каждая правка даёт
 * копии новый ключ, поэтому «Отменить» и вторая часть, записанная позже,
 * всегда находят свой пак. Копии кладут места, где настройки входят в кабинет
 * и на страницу (`rememberCustomStyleOf`); пак по ключу отдаёт
 * `getStylePackById` (`stylePacks.ts`).
 *
 * Файл в первом экране публичной страницы (его читает реестр паков), поэтому
 * каталог паков и шрифтов он не читает: что `basePackId` есть в каталоге,
 * сверяет `stylePacks.ts` (иначе — пак по умолчанию) и клиент ручек
 * (`api/orgDesigns.ts`).
 */
import type {
  ShadowLevel,
  SelectionMode,
  StylePack,
  StylePackDensity,
  StylePackId,
} from './stylePack.types';

/** Приставка ссылки своего стиля — та же, что у сервера и у своих шрифтов. */
export const CUSTOM_STYLE_PACK_PREFIX = 'custom:';
/** `custom:` + uuid v4 строчными — правило сервера `CUSTOM_STYLE_PACK_REF_RE`. */
export const CUSTOM_STYLE_PACK_REF_RE =
  /^custom:[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
/** Скругление своего стиля — целое 0–28. */
export const CUSTOM_STYLE_RADIUS_MAX = 28;
/** Имя своего стиля и копии — знаков. */
export const CUSTOM_STYLE_NAME_MAX = 60;

export type CustomStyleDensity = 's' | 'm' | 'l';

export interface CustomStyleShape {
  radius?: number;
  borderWidth?: 0 | 1 | 2;
  selectionMode?: SelectionMode;
  shadow?: ShadowLevel;
}

export interface CustomStylePaywall {
  stickyHeader?: boolean;
  dimUnselectedPlans?: boolean;
}

/** Правки пака — общие у тела своего стиля и у копии в воронке. */
export interface CustomStyleEdits {
  basePackId: StylePackId;
  shape?: CustomStyleShape;
  density?: CustomStyleDensity;
  paywall?: CustomStylePaywall;
}

/** Копия своего стиля в настройках воронки (`settings.customStylePack`). */
export interface CustomStylePackCopy extends CustomStyleEdits {
  id: string;
  name?: string;
  sourceId?: string;
}

/**
 * Тело своего стиля организации (`POST /organizations/:orgId/designs`,
 * `kind: 'style'`): правки пака, встроенный шрифт, шапка, кнопка и загрузчик
 * квиза — зеркало `orgStyleBodySchema` сервера. Разбор ответа — клиент ручек.
 */
export interface OrgStyleBody extends CustomStyleEdits {
  fontId?: string;
  quizHeader?: Record<string, unknown>;
  quizCta?: Record<string, unknown>;
  quizLoader?: Record<string, unknown>;
}

/**
 * Плотность (решено составителем задания): M — как у основы; S и L —
 * множители высоты варианта, его отступа по вертикали и зазора; округление до
 * целого, не меньше 0; прочие поля плотности — как у основы.
 */
export const CUSTOM_STYLE_DENSITY: Record<
  CustomStyleDensity,
  { optionHeight: number; optionPadY: number; gap: number }
> = {
  s: { optionHeight: 0.85, optionPadY: 0.75, gap: 0.75 },
  m: { optionHeight: 1, optionPadY: 1, gap: 1 },
  l: { optionHeight: 1.15, optionPadY: 1.25, gap: 1.25 },
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Raw = Record<string, unknown>;
const isObject = (v: unknown): v is Raw =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const onlyKeys = (v: Raw, keys: readonly string[]) =>
  Object.keys(v).every((k) => keys.includes(k));

export function isCustomStylePackRef(id: unknown): id is `custom:${string}` {
  return typeof id === 'string' && id.startsWith(CUSTOM_STYLE_PACK_PREFIX);
}

function readShape(v: unknown): CustomStyleShape | null {
  if (!isObject(v)) return null;
  if (!onlyKeys(v, ['radius', 'borderWidth', 'selectionMode', 'shadow']))
    return null;
  const { radius, borderWidth, selectionMode, shadow } = v;
  if (
    radius !== undefined &&
    !(
      Number.isInteger(radius) &&
      (radius as number) >= 0 &&
      (radius as number) <= CUSTOM_STYLE_RADIUS_MAX
    )
  )
    return null;
  if (borderWidth !== undefined && ![0, 1, 2].includes(borderWidth as number))
    return null;
  if (selectionMode !== undefined && !['border', 'ring'].includes(selectionMode as string))
    return null;
  if (shadow !== undefined && !['none', 'soft', 'lifted'].includes(shadow as string))
    return null;
  return v as CustomStyleShape;
}

function readPaywall(v: unknown): CustomStylePaywall | null {
  if (!isObject(v) || !onlyKeys(v, ['stickyHeader', 'dimUnselectedPlans']))
    return null;
  const ok = (x: unknown) => x === undefined || typeof x === 'boolean';
  return ok(v.stickyHeader) && ok(v.dimUnselectedPlans)
    ? (v as CustomStylePaywall)
    : null;
}

/**
 * Правки пака из сырого объекта: закрытая форма, пак-основа — строка, не
 * ссылка своего стиля (цепочек нет). `null` — форма неверна.
 */
export function readCustomStyleEdits(v: Raw): CustomStyleEdits | null {
  const { basePackId, shape, density, paywall } = v;
  if (typeof basePackId !== 'string' || isCustomStylePackRef(basePackId))
    return null;
  if (shape !== undefined && !readShape(shape)) return null;
  if (density !== undefined && !['s', 'm', 'l'].includes(density as string))
    return null;
  if (paywall !== undefined && !readPaywall(paywall)) return null;
  return {
    basePackId: basePackId as StylePackId,
    ...(shape !== undefined && { shape: shape as CustomStyleShape }),
    ...(density !== undefined && { density: density as CustomStyleDensity }),
    ...(paywall !== undefined && { paywall: paywall as CustomStylePaywall }),
  };
}

const COPY_KEYS = [
  'id',
  'name',
  'sourceId',
  'basePackId',
  'shape',
  'density',
  'paywall',
] as const;

/** Копия из сырого значения (закрытая форма сервера); `undefined` — не копия. */
export function readCustomStylePack(v: unknown): CustomStylePackCopy | undefined {
  if (!isObject(v) || !onlyKeys(v, COPY_KEYS)) return undefined;
  const { id, name, sourceId } = v;
  if (typeof id !== 'string' || !CUSTOM_STYLE_PACK_REF_RE.test(id))
    return undefined;
  if (
    name !== undefined &&
    !(typeof name === 'string' && name.length >= 1 && name.length <= CUSTOM_STYLE_NAME_MAX)
  )
    return undefined;
  if (sourceId !== undefined && !(typeof sourceId === 'string' && UUID_RE.test(sourceId)))
    return undefined;
  const edits = readCustomStyleEdits(v);
  if (!edits) return undefined;
  return {
    id,
    ...(name !== undefined && { name: name as string }),
    ...(sourceId !== undefined && { sourceId: sourceId as string }),
    ...edits,
  };
}

/**
 * Копия воронки: только когда `stylePackId` равен её `id`; иначе копия —
 * «сирота» и ничего не значит.
 */
export function customStyleOf(
  settings: object | null | undefined,
): CustomStylePackCopy | undefined {
  // любые настройки (вид редактора, ответ сервера) — поля читаются как неизвестные
  const raw = settings as Raw | null | undefined;
  if (!raw || !isCustomStylePackRef(raw.stylePackId)) return undefined;
  const copy = readCustomStylePack(raw.customStylePack);
  return copy && copy.id === raw.stylePackId ? copy : undefined;
}

/** Новый ключ копии: каждая правка своего стиля — новый `id`. */
export function newCustomStylePackId(): string {
  return CUSTOM_STYLE_PACK_PREFIX + crypto.randomUUID();
}

const copies = new Map<string, CustomStylePackCopy>();

/** Положить копию в реестр (только пополняется; ключ уже есть — копия та же). */
export function rememberCustomStylePack(copy: CustomStylePackCopy): void {
  if (!copies.has(copy.id)) copies.set(copy.id, copy);
}

/** Настройки вошли в кабинет или на страницу — их копию в реестр. */
export function rememberCustomStyleOf(settings: unknown): void {
  const copy = customStyleOf(isObject(settings) ? settings : undefined);
  if (copy) rememberCustomStylePack(copy);
}

/** Копия по ключу `custom:<uuid>` из реестра. */
export function customStylePackById(id: string): CustomStylePackCopy | undefined {
  return copies.get(id);
}

/** Только для тестов: забыть копии (память модуля, шрам WEB-2359). */
export function __resetCustomStylePacksForTests(): void {
  copies.clear();
}

const scaled = (base: number, k: number) => Math.max(0, Math.round(base * k));

/** Пак-основа с правками копии; `id` — пака-основы. */
export function buildCustomStylePack(
  base: StylePack,
  copy: CustomStyleEdits,
): StylePack {
  const s = copy.shape ?? {};
  const r = s.radius;
  const shape = {
    ...base.shape,
    ...(r !== undefined && {
      radiusCard: r,
      radiusOption: r,
      radiusImage: r,
      radiusButton: base.shape.radiusButton === 'pill' ? ('pill' as const) : r,
    }),
    ...(s.borderWidth !== undefined && { borderWidth: s.borderWidth }),
    ...(s.selectionMode !== undefined && { selectionMode: s.selectionMode }),
    ...(s.shadow !== undefined && { shadow: s.shadow }),
  };
  const k = copy.density ? CUSTOM_STYLE_DENSITY[copy.density] : undefined;
  const density: StylePackDensity = k
    ? {
        ...base.density,
        optionHeight: scaled(base.density.optionHeight, k.optionHeight),
        optionPadY: scaled(base.density.optionPadY, k.optionPadY),
        gap: scaled(base.density.gap, k.gap),
      }
    : base.density;
  const p = copy.paywall ?? {};
  const paywall = {
    ...base.paywall,
    ...(p.stickyHeader !== undefined && { stickyHeader: p.stickyHeader }),
    ...(p.dimUnselectedPlans !== undefined && {
      dimUnselectedPlans: p.dimUnselectedPlans,
    }),
  };
  return { ...base, shape, density, paywall };
}
